/**
 * Links that open one record inside the staff shell (TRUS-685): the links
 * WeaveIn bot cards, bell notifications and pushes carry for system events.
 *
 *   /members-view?id=<member>[&tab=tasks|documents|…]  member profile, a work tab
 *                                                    of its Program activities
 *   /members-view?id=<member>&tab=programs|consent|… member profile, a profile tab
 *                                                    (old keys such as enrollments,
 *                                                    doula-journey or giving are
 *                                                    aliased by the profile, TRUS-762)
 *   /tco-workflow-instance?id=<id>                   TCO (care) workflow instance
 *   /workflow-instances?instance=<id>                program workflow instance
 *   /approvals?request=<id>                          a signature request in the
 *                                                    Signature Center
 *   /v2-home                                         My Day (staff tasks); a plain tab
 *
 * The shell is tab-driven, so switching to `members-view` or
 * `tco-workflow-instance` alone renders nothing: those tabs need the record id
 * in App state. A link is therefore parsed here once, then "parked" and
 * announced: App sets its state and tab, and the page that shows the record
 * takes the parked request when it mounts (or hears the event if it is already
 * on screen). The same parser serves a full page load (App's boot effect).
 *
 * `/signatures?request=<id>` is accepted as an alias of `/approvals`: the
 * first TRUS-685 backend build emitted it, and notifications already written
 * with that link stay in people's bells.
 */

export const OPEN_RECORD_EVENT = 'weavein:open-record';

/** Work tabs of the member profile's Program activities workspace. */
export const MEMBER_WORK_TABS = [
  'case-episodes',
  'activities',
  'conversations',
  'outreach',
  'documents',
  'forms',
  'tasks',
  'mail',
] as const;
export type MemberWorkTab = (typeof MEMBER_WORK_TABS)[number];

export type RecordLink =
  | {
      kind: 'member';
      tab: 'members-view';
      memberId: number;
      workTab: MemberWorkTab | null;
      /** A profile tab (not a work tab) the link names; present only when it does. */
      profileTab?: string;
    }
  | { kind: 'tco-instance'; tab: 'tco-workflow-instance'; instanceId: number }
  | { kind: 'workflow-instance'; tab: 'workflow-instances'; instanceId: number }
  | { kind: 'signature'; tab: 'approvals'; requestId: number };

const posId = (v: string | null): number | null => (v && /^[1-9]\d{0,15}$/.test(v) ? Number(v) : null);

/** The record a path opens, or null when it is not one of these links (or has no usable id). */
export function parseRecordLink(pathname: string, search: string): RecordLink | null {
  const path = (pathname || '').replace(/^\/+/, '').replace(/\/+$/, '');
  const q = new URLSearchParams(search || '');
  switch (path) {
    case 'members-view': {
      const memberId = posId(q.get('id'));
      if (!memberId) return null;
      const tab = q.get('tab');
      const workTab = (MEMBER_WORK_TABS as readonly string[]).includes(tab || '') ? (tab as MemberWorkTab) : null;
      // Any other well-formed key is a profile tab; the profile resolves aliases
      // and ignores a key it does not know.
      const profileTab = !workTab && tab && /^[a-z][a-z0-9-]{0,40}$/.test(tab) ? tab : null;
      return {
        kind: 'member',
        tab: 'members-view',
        memberId,
        workTab,
        ...(profileTab ? { profileTab } : {}),
      };
    }
    case 'tco-workflow-instance': {
      const instanceId = posId(q.get('id'));
      return instanceId ? { kind: 'tco-instance', tab: 'tco-workflow-instance', instanceId } : null;
    }
    case 'workflow-instances': {
      const instanceId = posId(q.get('instance'));
      return instanceId ? { kind: 'workflow-instance', tab: 'workflow-instances', instanceId } : null;
    }
    case 'approvals':
    case 'signatures': {
      const requestId = posId(q.get('request'));
      return requestId ? { kind: 'signature', tab: 'approvals', requestId } : null;
    }
    default:
      return null;
  }
}

/** Where a record path goes when it carries no usable id. */
export function fallbackTabForRecordPath(pathname: string): string | null {
  const path = (pathname || '').replace(/^\/+/, '').replace(/\/+$/, '');
  if (path === 'members-view') return 'members';
  if (path === 'tco-workflow-instance') return 'tco-workflows';
  if (path === 'signatures') return 'approvals';
  return null;
}

// ---------------------------------------------------------------------------
// Parked requests: one per kind, fresh for a minute.
// ---------------------------------------------------------------------------

const PENDING_TTL_MS = 60_000;
const pending = new Map<RecordLink['kind'], { link: RecordLink; at: number }>();

/** Park the request and announce it (App switches tab / sets state; pages focus). */
export function requestOpenRecord(link: RecordLink): void {
  pending.set(link.kind, { link, at: Date.now() });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(OPEN_RECORD_EVENT, { detail: link }));
  }
}

/** Park without announcing (App's boot path sets its own state). */
export function parkRecord(link: RecordLink): void {
  pending.set(link.kind, { link, at: Date.now() });
}

/** Take (and clear) the parked request of a kind, if still fresh. */
export function takePendingRecord<K extends RecordLink['kind']>(kind: K): Extract<RecordLink, { kind: K }> | null {
  const entry = pending.get(kind);
  pending.delete(kind);
  if (!entry || Date.now() - entry.at > PENDING_TTL_MS) return null;
  return entry.link as Extract<RecordLink, { kind: K }>;
}

/**
 * Subscribe a mounted page to requests of one kind. The handler returns true
 * when it acted on the request (which then stops being parked); false leaves
 * it for whoever mounts next (e.g. a profile still showing another member).
 */
export function onOpenRecord<K extends RecordLink['kind']>(
  kind: K,
  handler: (link: Extract<RecordLink, { kind: K }>) => boolean,
): () => void {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => {
    const link = (e as CustomEvent<RecordLink>).detail;
    if (link?.kind === kind && handler(link as Extract<RecordLink, { kind: K }>)) pending.delete(kind);
  };
  window.addEventListener(OPEN_RECORD_EVENT, listener);
  return () => window.removeEventListener(OPEN_RECORD_EVENT, listener);
}
